import React, { useEffect, useRef, useState } from "react";
import { Check, Copy } from "lucide-react";

type CopyState = "idle" | "copied" | "failed";

async function writeClipboard(text: string) {
  if (navigator.clipboard?.writeText) {
    // Some embedded browsers leave this promise pending behind a permission
    // prompt, so give it a moment before falling back.
    const ok = await Promise.race([
      navigator.clipboard.writeText(text).then(() => true, () => false),
      new Promise<boolean>((resolve) => window.setTimeout(() => resolve(false), 800)),
    ]);
    if (ok) return;
  }
  // Older Safari, non-secure contexts and restricted webviews.
  const area = document.createElement("textarea");
  area.value = text;
  area.setAttribute("readonly", "");
  area.style.position = "fixed";
  area.style.opacity = "0";
  document.body.appendChild(area);
  area.select();
  const ok = document.execCommand("copy");
  area.remove();
  if (!ok) throw new Error("Copy command was rejected");
}

export default function CopyButton({
  getText,
  label = "Copy",
  className = "",
}: {
  getText: () => string;
  label?: string;
  className?: string;
}) {
  const [state, setState] = useState<CopyState>("idle");
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = async () => {
    window.clearTimeout(timer.current);
    try {
      await writeClipboard(getText());
      setState("copied");
    } catch {
      setState("failed");
    }
    timer.current = window.setTimeout(() => setState("idle"), 1600);
  };

  const text = state === "copied" ? "Copied" : state === "failed" ? "Copy failed" : label;
  return (
    <button
      type="button"
      onClick={copy}
      className={`copy-button ${state !== "idle" ? `is-${state}` : ""} ${className}`}
      aria-label={state === "idle" ? `${label} to clipboard` : text}
    >
      <span className="copy-button-icon" aria-hidden="true">
        <Copy size={13} className="copy-icon-idle" />
        <Check size={13} strokeWidth={2.5} className="copy-icon-done" />
      </span>
      {/* The widest label reserves the width, so the button never shifts. */}
      <span className="copy-button-label" data-sizer={label.length > 11 ? label : "Copy failed"}>
        <span aria-live="polite">{text}</span>
      </span>
    </button>
  );
}
